<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Address Input Component
      p.text--secondary A comprehensive Malaysian address input component with postcode lookup, smart validation, and .sync modifier support.

    // Basic Usage Examples
    .component-section.mb-8#basic-usage
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-usage')") Basic Usage
      .variations-grid
        .variation-item.mb-6
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection('basic-usage')") Standard Address Input
            p.text--secondary.caption Default general address input with type prop options

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-4
              h5.mb-2 Basic Address Input (General Mode - Default)
              p.text--secondary.mb-2 Default behavior with flexible state/country fields
              yz-address-input(:line1.sync="basicAddress.line1" :line2.sync="basicAddress.line2" :postcode.sync="basicAddress.postcode" :city.sync="basicAddress.city" :state.sync="basicAddress.state" :country.sync="basicAddress.country" :style="{ maxWidth: '500px' }")
              .mt-3
                small.text--secondary Address Object: {{ JSON.stringify(basicAddress, null, 2) }}

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === basicUsageCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === basicUsageCode ? 'success' : ''" @click="copyCode(basicUsageCode)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ basicUsageCode }}

    // Malaysia-Specific Address Input
    .component-section.mb-8#malaysia-address
      h3.mb-4.cursor-pointer(@click="navigateToSection('malaysia-address')") Malaysia-Specific Address Input
      .variations-grid
        .variation-item.mb-6
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection('malaysia-address')") Malaysian Address with Smart Features
            p.text--secondary.caption Malaysian states dropdown, auto-country setting, and 5-digit postcode validation with lookup

          .variation-preview.mb-4.pa-4.border.rounded
            .row
              .col-md-6.mb-4
                h5.mb-2 Malaysia Address (Required)
                p.text--secondary.mb-2 Features: Malaysian states dropdown, auto-set country to "Malaysia", 5-digit postcode validation
                yz-address-input(:line1.sync="malaysiaAddresses.required.line1" :line2.sync="malaysiaAddresses.required.line2" :postcode.sync="malaysiaAddresses.required.postcode" :city.sync="malaysiaAddresses.required.city" :state.sync="malaysiaAddresses.required.state" :country.sync="malaysiaAddresses.required.country" type="malaysia" required :style="{ maxWidth: '400px' }")
                .mt-2
                  small.text--secondary.font-weight-medium Try these Malaysian postcodes:
                .mt-1
                  .d-flex.flex-wrap.gap-2
                    yz-btn(v-for="sample in malaysiaSamplePostcodes" :key="sample.postcode" size="xs" outlined :label="sample.postcode" @click="setMalaysiaPostcode('required', sample.postcode)")
                .mt-3
                  small.text--secondary Required Malaysia Address: {{ JSON.stringify(malaysiaAddresses.required, null, 2) }}

              .col-md-6.mb-4
                h5.mb-2 Malaysia Address (Optional)
                p.text--secondary.mb-2 Same features but no required validation - shows flexible usage
                yz-address-input(:line1.sync="malaysiaAddresses.optional.line1" :line2.sync="malaysiaAddresses.optional.line2" :postcode.sync="malaysiaAddresses.optional.postcode" :city.sync="malaysiaAddresses.optional.city" :state.sync="malaysiaAddresses.optional.state" :country.sync="malaysiaAddresses.optional.country" type="malaysia" :style="{ maxWidth: '400px' }")
                .mt-2
                  small.text--secondary.font-weight-medium Try these Malaysian postcodes:
                .mt-1
                  .d-flex.flex-wrap.gap-2
                    yz-btn(v-for="sample in malaysiaSamplePostcodes" :key="sample.postcode" size="xs" outlined :label="sample.postcode" @click="setMalaysiaPostcode('optional', sample.postcode)")
                .mt-3
                  small.text--secondary Optional Malaysia Address: {{ JSON.stringify(malaysiaAddresses.optional, null, 2) }}

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === malaysiaTypeCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === malaysiaTypeCode ? 'success' : ''" @click="copyCode(malaysiaTypeCode)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ malaysiaTypeCode }}

    // General International Address Input
    .component-section.mb-8#general-address
      h3.mb-4.cursor-pointer(@click="navigateToSection('general-address')") General International Address Input
      .variations-grid
        .variation-item.mb-6
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection('general-address')") International Address with Flexible Fields
            p.text--secondary.caption Free-form state and country fields for international addressing

          .variation-preview.mb-4.pa-4.border.rounded
            .row
              .col-md-6.mb-4
                h5.mb-2 General Address (Required)
                p.text--secondary.mb-2 Features: Text input for state, manual country selection, flexible postcode validation
                yz-address-input(:line1.sync="generalAddresses.required.line1" :line2.sync="generalAddresses.required.line2" :postcode.sync="generalAddresses.required.postcode" :city.sync="generalAddresses.required.city" :state.sync="generalAddresses.required.state" :country.sync="generalAddresses.required.country" type="general" required :style="{ maxWidth: '400px' }")
                .mt-2
                  small.text--secondary.font-weight-medium Sample international data:
                .mt-1
                  .d-flex.flex-wrap.gap-2
                    yz-btn(v-for="sample in internationalSamples" :key="sample.country" size="xs" outlined :label="sample.country" @click="setInternationalSample('required', sample)")
                .mt-3
                  small.text--secondary Required General Address: {{ JSON.stringify(generalAddresses.required, null, 2) }}

              .col-md-6.mb-4
                h5.mb-2 General Address (Optional)
                p.text--secondary.mb-2 Flexible international addressing without required validation
                yz-address-input(:line1.sync="generalAddresses.optional.line1" :line2.sync="generalAddresses.optional.line2" :postcode.sync="generalAddresses.optional.postcode" :city.sync="generalAddresses.optional.city" :state.sync="generalAddresses.optional.state" :country.sync="generalAddresses.optional.country" type="general" :style="{ maxWidth: '400px' }")
                .mt-2
                  small.text--secondary.font-weight-medium Sample international data:
                .mt-1
                  .d-flex.flex-wrap.gap-2
                    yz-btn(v-for="sample in internationalSamples" :key="sample.country" size="xs" outlined :label="sample.country" @click="setInternationalSample('optional', sample)")
                .mt-3
                  small.text--secondary Optional General Address: {{ JSON.stringify(generalAddresses.optional, null, 2) }}

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === generalTypeCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === generalTypeCode ? 'success' : ''" @click="copyCode(generalTypeCode)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ generalTypeCode }}

    // Practical Validation Examples
    .component-section.mb-8#practical-validation
      h3.mb-4.cursor-pointer(@click="navigateToSection('practical-validation')") Practical Validation Patterns
      .variations-grid
        .variation-item.mb-6
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection('practical-validation')") Common Usage Scenarios
            p.text--secondary.caption Real-world validation patterns with different address types

          .variation-preview.mb-4.pa-4.border.rounded
            .row
              .col-md-6.mb-4
                h5.mb-2 Required Malaysia Address
                p.text--secondary.mb-2 Malaysian dropdown states, auto country, 5-digit postcode validation
                v-form(ref="requiredMalaysiaForm")
                  yz-address-input(:line1.sync="practicalExamples.requiredMalaysia.line1" :line2.sync="practicalExamples.requiredMalaysia.line2" :postcode.sync="practicalExamples.requiredMalaysia.postcode" :city.sync="practicalExamples.requiredMalaysia.city" :state.sync="practicalExamples.requiredMalaysia.state" :country.sync="practicalExamples.requiredMalaysia.country" type="malaysia" required :style="{ maxWidth: '400px' }")
                .mt-2
                  .d-flex.gap-2
                    yz-btn(label="Validate" size="sm" @click="validateRequiredMalaysiaForm")
                    yz-btn(label="Reset" size="sm" outlined @click="resetRequiredMalaysiaForm")
                .mt-2(v-if="requiredMalaysiaFormValidationResult")
                  span.yz-regular-body-xs(:class="{ 'success--text': requiredMalaysiaFormValidationResult === 'Valid', 'error--text': requiredMalaysiaFormValidationResult === 'Invalid' }") {{ requiredMalaysiaFormValidationResult }}
                .mt-2
                  small.text--secondary Required Malaysia: {{ JSON.stringify(practicalExamples.requiredMalaysia, null, 2) }}

              .col-md-6.mb-4
                h5.mb-2 Optional Malaysia Address
                p.text--secondary.mb-2 Same Malaysian features but no required validation
                v-form(ref="optionalMalaysiaForm")
                  yz-address-input(:line1.sync="practicalExamples.optionalMalaysia.line1" :line2.sync="practicalExamples.optionalMalaysia.line2" :postcode.sync="practicalExamples.optionalMalaysia.postcode" :city.sync="practicalExamples.optionalMalaysia.city" :state.sync="practicalExamples.optionalMalaysia.state" :country.sync="practicalExamples.optionalMalaysia.country" type="malaysia" :style="{ maxWidth: '400px' }")
                .mt-2
                  .d-flex.gap-2
                    yz-btn(label="Validate" size="sm" @click="validateOptionalMalaysiaForm")
                    yz-btn(label="Reset" size="sm" outlined @click="resetOptionalMalaysiaForm")
                .mt-2(v-if="optionalMalaysiaFormValidationResult")
                  span.yz-regular-body-xs(:class="{ 'success--text': optionalMalaysiaFormValidationResult === 'Valid', 'error--text': optionalMalaysiaFormValidationResult === 'Invalid' }") {{ optionalMalaysiaFormValidationResult }}
                .mt-2
                  small.text--secondary Optional Malaysia: {{ JSON.stringify(practicalExamples.optionalMalaysia, null, 2) }}

            .row.mt-4
              .col-md-6.mb-4
                h5.mb-2 Required General Address
                p.text--secondary.mb-2 International addressing with flexible fields and required validation
                v-form(ref="requiredGeneralForm")
                  yz-address-input(:line1.sync="practicalExamples.requiredGeneral.line1" :line2.sync="practicalExamples.requiredGeneral.line2" :postcode.sync="practicalExamples.requiredGeneral.postcode" :city.sync="practicalExamples.requiredGeneral.city" :state.sync="practicalExamples.requiredGeneral.state" :country.sync="practicalExamples.requiredGeneral.country" type="general" required :style="{ maxWidth: '400px' }")
                .mt-2
                  .d-flex.gap-2
                    yz-btn(label="Validate" size="sm" @click="validateRequiredGeneralForm")
                    yz-btn(label="Reset" size="sm" outlined @click="resetRequiredGeneralForm")
                .mt-2(v-if="requiredGeneralFormValidationResult")
                  span.yz-regular-body-xs(:class="{ 'success--text': requiredGeneralFormValidationResult === 'Valid', 'error--text': requiredGeneralFormValidationResult === 'Invalid' }") {{ requiredGeneralFormValidationResult }}
                .mt-2
                  small.text--secondary Required General: {{ JSON.stringify(practicalExamples.requiredGeneral, null, 2) }}

              .col-md-6.mb-4
                h5.mb-2 Optional General Address
                p.text--secondary.mb-2 International addressing with no required validation
                v-form(ref="optionalGeneralForm")
                  yz-address-input(:line1.sync="practicalExamples.optionalGeneral.line1" :line2.sync="practicalExamples.optionalGeneral.line2" :postcode.sync="practicalExamples.optionalGeneral.postcode" :city.sync="practicalExamples.optionalGeneral.city" :state.sync="practicalExamples.optionalGeneral.state" :country.sync="practicalExamples.optionalGeneral.country" type="general" :style="{ maxWidth: '400px' }")
                .mt-2
                  .d-flex.gap-2
                    yz-btn(label="Validate" size="sm" @click="validateOptionalGeneralForm")
                    yz-btn(label="Reset" size="sm" outlined @click="resetOptionalGeneralForm")
                .mt-2(v-if="optionalGeneralFormValidationResult")
                  span.yz-regular-body-xs(:class="{ 'success--text': optionalGeneralFormValidationResult === 'Valid', 'error--text': optionalGeneralFormValidationResult === 'Invalid' }") {{ optionalGeneralFormValidationResult }}
                .mt-2
                  small.text--secondary Optional General: {{ JSON.stringify(practicalExamples.optionalGeneral, null, 2) }}

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === practicalValidationCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === practicalValidationCode ? 'success' : ''" @click="copyCode(practicalValidationCode)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ practicalValidationCode }}

    // V-form Integration
    .component-section.mb-8#vform-integration
      h3.mb-4.cursor-pointer(@click="navigateToSection('vform-integration')") V-form Integration
      .variations-grid
        .variation-item.mb-6
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection('vform-integration')") Multiple Address Form
            p.text--secondary.caption Billing and shipping addresses with full form validation

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-4
              h5.mb-2 Customer Information Form
              v-form(ref="customerForm")
                .row
                  .col-md-6.mb-4
                    .mb-3
                      label.yz-regular-body-sm.font-weight-medium.mb-2 Customer Name
                      yz-text-field(v-model="customerName" placeholder="Enter customer name..." required required-error-message="Customer name is required" :rules="[v => (v && v.length >= 2) || 'Name must be at least 2 characters']" clearable)
                    .mb-3
                      label.yz-regular-body-sm.font-weight-medium.mb-2 Email
                      yz-text-field(v-model="customerEmail" placeholder="Enter email address..." type="email" required required-error-message="Email is required" :rules="[v => /.+@.+\..+/.test(v) || 'Email must be valid']" clearable)
                  .col-md-6.mb-4
                    .mb-3
                      label.yz-regular-body-sm.font-weight-medium.mb-2 Phone Number
                      yz-text-field(v-model="customerPhone" placeholder="Enter phone number..." type="tel" required required-error-message="Phone number is required" :rules="[v => (v && v.length >= 10) || 'Phone number must be at least 10 digits']" clearable)
                .row
                  .col-md-6.mb-4
                    h6.mb-2 Billing Address
                    yz-address-input(:line1.sync="billingAddress.line1" :line2.sync="billingAddress.line2" :postcode.sync="billingAddress.postcode" :city.sync="billingAddress.city" :state.sync="billingAddress.state" required)
                  .col-md-6.mb-4
                    h6.mb-2 Shipping Address
                    .mb-2
                      yz-checkbox(v-model="sameAsBilling" label="Same as billing address")
                    yz-address-input(v-if="!sameAsBilling" :line1.sync="shippingAddress.line1" :line2.sync="shippingAddress.line2" :postcode.sync="shippingAddress.postcode" :city.sync="shippingAddress.city" :state.sync="shippingAddress.state")
                    .text--secondary(v-else) Shipping address will be same as billing address
                .mt-4
                  .d-flex.gap-2
                    yz-btn(label="Reset Form" size="sm" @click="resetCustomerForm")
                    yz-btn(label="Validate Form" size="sm" outlined @click="validateCustomerForm")
                  .mt-2(v-if="customerFormValidationResult")
                    span.yz-regular-body-xs(:class="{ 'success--text': customerFormValidationResult === 'Valid', 'error--text': customerFormValidationResult === 'Invalid' }") {{ customerFormValidationResult }}

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === vformCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === vformCode ? 'success' : ''" @click="copyCode(vformCode)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ vformCode }}

    // Size Variants
    .component-section.mb-8#size-variants
      h3.mb-4.cursor-pointer(@click="navigateToSection('size-variants')") Size Variants
      .variations-grid
        .variation-item.mb-6
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection('size-variants')") Different Sizes
            p.text--secondary.caption Large (default), medium, and small sizes

          .variation-preview.mb-4.pa-4.border.rounded
            .row
              .col-md-4.mb-4
                h5.mb-2 Large (Default)
                yz-address-input(:line1.sync="sizeAddresses.large.line1" :line2.sync="sizeAddresses.large.line2" :postcode.sync="sizeAddresses.large.postcode" :city.sync="sizeAddresses.large.city" :state.sync="sizeAddresses.large.state" size="lg")
              .col-md-4.mb-4
                h5.mb-2 Medium
                yz-address-input(:line1.sync="sizeAddresses.medium.line1" :line2.sync="sizeAddresses.medium.line2" :postcode.sync="sizeAddresses.medium.postcode" :city.sync="sizeAddresses.medium.city" :state.sync="sizeAddresses.medium.state" size="md")
              .col-md-4.mb-4
                h5.mb-2 Small
                yz-address-input(:line1.sync="sizeAddresses.small.line1" :line2.sync="sizeAddresses.small.line2" :postcode.sync="sizeAddresses.small.postcode" :city.sync="sizeAddresses.small.city" :state.sync="sizeAddresses.small.state" size="sm")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === sizesCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === sizesCode ? 'success' : ''" @click="copyCode(sizesCode)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ sizesCode }}

    // State Examples
    .component-section.mb-8#state-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('state-examples')") Component States
      .variations-grid
        .variation-item.mb-6
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection('state-examples')") Different States
            p.text--secondary.caption Readonly, disabled, and loading states

          .variation-preview.mb-4.pa-4.border.rounded
            .row
              .col-md-4.mb-4
                h5.mb-2 Readonly
                yz-address-input(:line1.sync="stateAddresses.readonly.line1" :line2.sync="stateAddresses.readonly.line2" :postcode.sync="stateAddresses.readonly.postcode" :city.sync="stateAddresses.readonly.city" :state.sync="stateAddresses.readonly.state", :country.sync="stateAddresses.readonly.country" readonly)
              .col-md-4.mb-4
                h5.mb-2 Disabled
                yz-address-input(:line1.sync="stateAddresses.disabled.line1" :line2.sync="stateAddresses.disabled.line2" :postcode.sync="stateAddresses.disabled.postcode" :city.sync="stateAddresses.disabled.city" :state.sync="stateAddresses.disabled.state", :country.sync="stateAddresses.disabled.country" disabled)

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === statesCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === statesCode ? 'success' : ''" @click="copyCode(statesCode)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ statesCode }}

    // Postcode Lookup Demo
    .component-section.mb-8#postcode-lookup
      h3.mb-4.cursor-pointer(@click="navigateToSection('postcode-lookup')") Postcode Lookup Feature
      .variations-grid
        .variation-item.mb-6
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection('postcode-lookup')") Automatic City and State Population
            p.text--secondary.caption Enter a 5-digit Malaysian postcode to see automatic city and state population

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-4
              h5.mb-2 Try These Postcodes
              .mb-2
                .d-flex.flex-wrap.gap-2
                  yz-btn(v-for="sample in samplePostcodes" :key="sample.postcode" size="sm" outlined :label="`${sample.postcode} (${sample.city})`" @click="setPostcode(sample.postcode)")
              yz-address-input(:line1.sync="postcodeAddress.line1" :line2.sync="postcodeAddress.line2" :postcode.sync="postcodeAddress.postcode" :city.sync="postcodeAddress.city" :state.sync="postcodeAddress.state" type="malaysia" :style="{ maxWidth: '500px' }")
              .mt-3
                small.text--secondary.font-weight-medium Instructions:
                small.text--secondary Enter a 5-digit postcode and watch the city and state auto-populate
              .mt-2
                small.text--secondary Current Address: {{ JSON.stringify(postcodeAddress, null, 2) }}

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === postcodeCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === postcodeCode ? 'success' : ''" @click="copyCode(postcodeCode)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ postcodeCode }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API

      h4.mb-3 Props
      .props-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in addressInputProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

      h4.mb-3 Events
      .events-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Event
                th Parameters
                th Description
            tbody
              tr(v-for="event in addressInputEvents" :key="event.name")
                td.font-weight-medium.text-no-wrap {{ event.name }}
                td.text--secondary.text-no-wrap {{ event.parameters }}
                td.single-line {{ event.description }}

      h4.mb-3 Methods
      .methods-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Method
                th Parameters
                th Returns
                th Description
            tbody
              tr(v-for="method in addressInputMethods" :key="method.name")
                td.font-weight-medium.text-no-wrap {{ method.name }}
                td.text--secondary.text-no-wrap {{ method.parameters }}
                td.text--secondary.text-no-wrap {{ method.returns }}
                td.single-line {{ method.description }}

      h4.mb-3 Computed Properties
      .computed-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Property
                th Type
                th Description
            tbody
              tr(v-for="computed in addressInputComputed" :key="computed.name")
                td.font-weight-medium.text-no-wrap {{ computed.name }}
                td.text--secondary.text-no-wrap {{ computed.type }}
                td.single-line {{ computed.description }}
</template>

<script>
export default {
  data() {
    return {
      copiedCode: null,
      // Address data for different examples
      basicAddress: {
        line1: '',
        line2: '',
        postcode: '',
        city: '',
        state: '',
        country: ''
      },
      requiredAddress: {
        line1: '',
        line2: '',
        postcode: '',
        city: '',
        state: ''
      },
      optionalAddress: {
        line1: '',
        line2: '',
        postcode: '',
        city: '',
        state: ''
      },
      // V-form data
      customerName: '',
      customerEmail: '',
      customerPhone: '',
      billingAddress: {
        line1: '',
        line2: '',
        postcode: '',
        city: '',
        state: ''
      },
      shippingAddress: {
        line1: '',
        line2: '',
        postcode: '',
        city: '',
        state: ''
      },
      sameAsBilling: false,
      customerFormValidationResult: null,
      // Practical examples data
      practicalExamples: {
        requiredMalaysia: {
          line1: '',
          line2: '',
          postcode: '',
          city: '',
          state: '',
          country: ''
        },
        optionalMalaysia: {
          line1: '',
          line2: '',
          postcode: '',
          city: '',
          state: '',
          country: 'Malaysia'
        },
        requiredGeneral: {
          line1: '',
          line2: '',
          postcode: '',
          city: '',
          state: '',
          country: ''
        },
        optionalGeneral: {
          line1: '',
          line2: '',
          postcode: '',
          city: '',
          state: '',
          country: ''
        }
      },
      requiredMalaysiaFormValidationResult: null,
      optionalMalaysiaFormValidationResult: null,
      requiredGeneralFormValidationResult: null,
      optionalGeneralFormValidationResult: null,
      // Size variants data
      sizeAddresses: {
        large: {
          line1: 'Large size address',
          line2: '',
          postcode: '',
          city: '',
          state: ''
        },
        medium: {
          line1: 'Medium size address',
          line2: '',
          postcode: '',
          city: '',
          state: ''
        },
        small: {
          line1: 'Small size address',
          line2: '',
          postcode: '',
          city: '',
          state: ''
        }
      },
      // State examples data
      stateAddresses: {
        readonly: {
          line1: '123 Main Street',
          line2: 'Suite 100',
          postcode: '50200',
          city: 'Kuala Lumpur',
          state: 'WP Kuala Lumpur',
          country: 'Malaysia'
        },
        disabled: {
          line1: '456 Business Avenue',
          line2: 'Floor 5',
          postcode: '10400',
          city: 'Georgetown',
          state: 'Pulau Pinang',
          country: 'Malaysia'
        },
      },
      // Postcode lookup demo
      postcodeAddress: {
        line1: '',
        line2: '',
        postcode: '',
        city: '',
        state: ''
      },
      samplePostcodes: [
        { postcode: '50200', city: 'Kuala Lumpur' },
        { postcode: '10400', city: 'Georgetown' },
        { postcode: '80100', city: 'Johor Bahru' },
        { postcode: '20000', city: 'Kuala Terengganu' },
        { postcode: '93350', city: 'Kuching' }
      ],
      // Malaysia-specific addresses data
      malaysiaAddresses: {
        required: {
          line1: '',
          line2: '',
          postcode: '',
          city: '',
          state: '',
          country: ''
        },
        optional: {
          line1: '',
          line2: '',
          postcode: '',
          city: '',
          state: '',
          country: ''
        }
      },
      malaysiaSamplePostcodes: [
        { postcode: '50200', city: 'Kuala Lumpur', state: 'WP Kuala Lumpur' },
        { postcode: '10400', city: 'Georgetown', state: 'Pulau Pinang' },
        { postcode: '80100', city: 'Johor Bahru', state: 'Johor' },
        { postcode: '20000', city: 'Kuala Terengganu', state: 'Terengganu' },
        { postcode: '93350', city: 'Kuching', state: 'Sarawak' },
        { postcode: '05000', city: 'Alor Setar', state: 'Kedah' }
      ],
      // General international addresses data
      generalAddresses: {
        required: {
          line1: '',
          line2: '',
          postcode: '',
          city: '',
          state: '',
          country: ''
        },
        optional: {
          line1: '',
          line2: '',
          postcode: '',
          city: '',
          state: '',
          country: ''
        }
      },
      internationalSamples: [
        { country: 'USA', state: 'California', city: 'San Francisco', postcode: '94102' },
        { country: 'UK', state: 'England', city: 'London', postcode: 'SW1A 1AA' },
        { country: 'Canada', state: 'Ontario', city: 'Toronto', postcode: 'M5H 2N2' },
        { country: 'Australia', state: 'NSW', city: 'Sydney', postcode: '2000' },
        { country: 'Singapore', state: 'Central', city: 'Singapore', postcode: '018989' }
      ],
      // Code examples
      basicUsageCode: `// Template - General Mode (Default)
yz-address-input(
  :line1.sync="address.line1"
  :line2.sync="address.line2"
  :postcode.sync="address.postcode"
  :city.sync="address.city"
  :state.sync="address.state"
  :country.sync="address.country"
  type="general"  // Default - can be omitted
)

// Template - Malaysia Mode
yz-address-input(
  :line1.sync="address.line1"
  :line2.sync="address.line2"
  :postcode.sync="address.postcode"
  :city.sync="address.city"
  :state.sync="address.state"
  :country.sync="address.country"
  type="malaysia"  // Malaysian features enabled
)

// Script
data() {
  return {
    address: {
      line1: '',
      line2: '',
      postcode: '',
      city: '',
      state: '',
      country: ''
    }
  }
}`,
      vformCode: `// Template
v-form(ref="customerForm")
  // Customer details
  yz-text-field(v-model="customerName" required)
  yz-text-field(v-model="customerEmail" type="email" required)
  yz-text-field(v-model="customerPhone" type="tel" required)

  // Billing address
  h6 Billing Address
  yz-address-input(
    :line1.sync="billingAddress.line1"
    :line2.sync="billingAddress.line2"
    :postcode.sync="billingAddress.postcode"
    :city.sync="billingAddress.city"
    :state.sync="billingAddress.state"
    required
  )

  // Shipping address
  h6 Shipping Address
  yz-checkbox(v-model="sameAsBilling" label="Same as billing address")
  yz-address-input(
    v-if="!sameAsBilling"
    :line1.sync="shippingAddress.line1"
    :line2.sync="shippingAddress.line2"
    :postcode.sync="shippingAddress.postcode"
    :city.sync="shippingAddress.city"
    :state.sync="shippingAddress.state"
  )

// Script
methods: {
  validateForm() {
    return this.$refs.customerForm.validate()
  },
  resetForm() {
    this.$refs.customerForm.reset()
  }
}`,
      sizesCode: `// Large (default)
yz-address-input(
  :line1.sync="address.line1"
  :postcode.sync="address.postcode"
  // ... other props
  size="lg"
)

// Medium
yz-address-input(
  :line1.sync="address.line1"
  :postcode.sync="address.postcode"
  // ... other props
  size="md"
)

// Small
yz-address-input(
  :line1.sync="address.line1"
  :postcode.sync="address.postcode"
  // ... other props
  size="sm"
)`,
      statesCode: `// Readonly state
yz-address-input(
  :line1.sync="address.line1"
  :postcode.sync="address.postcode"
  // ... other props
  readonly
)

// Disabled state
yz-address-input(
  :line1.sync="address.line1"
  :postcode.sync="address.postcode"
  // ... other props
  disabled
)
`,
      postcodeCode: `// Automatic postcode lookup
yz-address-input(
  :line1.sync="address.line1"
  :line2.sync="address.line2"
  :postcode.sync="address.postcode"
  :city.sync="address.city"
  :state.sync="address.state"
)

// When user enters a 5-digit postcode, the component automatically:
// 1. Makes API call to /shipping/postcodes?postcode={value}
// 2. Auto-populates city and state if data is found
// 3. Shows loading indicators during API call`,
      practicalValidationCode: `// Template - All Type & Validation Combinations (Pug syntax)
template(lang="pug")
  div
    .row
      .col-md-6
        h5 Required Malaysia Address
        p.text--secondary Malaysian states dropdown, auto country, 5-digit validation
        v-form(ref="requiredMalaysiaForm")
          yz-address-input(
            :line1.sync="practicalExamples.requiredMalaysia.line1"
            :line2.sync="practicalExamples.requiredMalaysia.line2"
            :postcode.sync="practicalExamples.requiredMalaysia.postcode"
            :city.sync="practicalExamples.requiredMalaysia.city"
            :state.sync="practicalExamples.requiredMalaysia.state"
            :country.sync="practicalExamples.requiredMalaysia.country"
            type="malaysia"
            required
          )
        .mt-2
          yz-btn(label="Validate" @click="validateRequiredMalaysiaForm")
          yz-btn(label="Reset" outlined @click="resetRequiredMalaysiaForm")

      .col-md-6
        h5 Optional Malaysia Address
        p.text--secondary Same Malaysian features but no required validation
        v-form(ref="optionalMalaysiaForm")
          yz-address-input(
            :line1.sync="practicalExamples.optionalMalaysia.line1"
            :line2.sync="practicalExamples.optionalMalaysia.line2"
            :postcode.sync="practicalExamples.optionalMalaysia.postcode"
            :city.sync="practicalExamples.optionalMalaysia.city"
            :state.sync="practicalExamples.optionalMalaysia.state"
            :country.sync="practicalExamples.optionalMalaysia.country"
            type="malaysia"
          )
        .mt-2
          yz-btn(label="Validate" @click="validateOptionalMalaysiaForm")
          yz-btn(label="Reset" outlined @click="resetOptionalMalaysiaForm")

    .row.mt-4
      .col-md-6
        h5 Required General Address
        p.text--secondary International addressing with required validation
        v-form(ref="requiredGeneralForm")
          yz-address-input(
            :line1.sync="practicalExamples.requiredGeneral.line1"
            :line2.sync="practicalExamples.requiredGeneral.line2"
            :postcode.sync="practicalExamples.requiredGeneral.postcode"
            :city.sync="practicalExamples.requiredGeneral.city"
            :state.sync="practicalExamples.requiredGeneral.state"
            :country.sync="practicalExamples.requiredGeneral.country"
            type="general"
            required
          )
        .mt-2
          yz-btn(label="Validate" @click="validateRequiredGeneralForm")
          yz-btn(label="Reset" outlined @click="resetRequiredGeneralForm")

      .col-md-6
        h5 Optional General Address
        p.text--secondary International addressing with no required validation
        v-form(ref="optionalGeneralForm")
          yz-address-input(
            :line1.sync="practicalExamples.optionalGeneral.line1"
            :line2.sync="practicalExamples.optionalGeneral.line2"
            :postcode.sync="practicalExamples.optionalGeneral.postcode"
            :city.sync="practicalExamples.optionalGeneral.city"
            :state.sync="practicalExamples.optionalGeneral.state"
            :country.sync="practicalExamples.optionalGeneral.country"
            type="general"
          )
        .mt-2
          yz-btn(label="Validate" @click="validateOptionalGeneralForm")
          yz-btn(label="Reset" outlined @click="resetOptionalGeneralForm")

// Script - Data and methods for all four scenarios
script
export default {
  data() {
    return {
      practicalExamples: {
        requiredMalaysia: { line1: '', line2: '', postcode: '', city: '', state: '', country: '' },
        optionalMalaysia: { line1: '', line2: '', postcode: '', city: '', state: '', country: '' },
        requiredGeneral: { line1: '', line2: '', postcode: '', city: '', state: '', country: '' },
        optionalGeneral: { line1: '', line2: '', postcode: '', city: '', state: '', country: '' }
      },
      requiredMalaysiaFormValidationResult: null,
      optionalMalaysiaFormValidationResult: null,
      requiredGeneralFormValidationResult: null,
      optionalGeneralFormValidationResult: null
    }
  },
  methods: {
    // Malaysia Required
    validateRequiredMalaysiaForm() {
      const isValid = this.$refs.requiredMalaysiaForm.validate();
      this.requiredMalaysiaFormValidationResult = isValid ? 'Valid' : 'Invalid';
    },
    resetRequiredMalaysiaForm() {
      this.practicalExamples.requiredMalaysia = { line1: '', line2: '', postcode: '', city: '', state: '', country: '' };
      this.$refs.requiredMalaysiaForm.resetValidation();
    },
    // Malaysia Optional
    validateOptionalMalaysiaForm() {
      const isValid = this.$refs.optionalMalaysiaForm.validate();
      this.optionalMalaysiaFormValidationResult = isValid ? 'Valid' : 'Invalid';
    },
    resetOptionalMalaysiaForm() {
      this.practicalExamples.optionalMalaysia = { line1: '', line2: '', postcode: '', city: '', state: '', country: '' };
      this.$refs.optionalMalaysiaForm.resetValidation();
    },
    // General Required
    validateRequiredGeneralForm() {
      const isValid = this.$refs.requiredGeneralForm.validate();
      this.requiredGeneralFormValidationResult = isValid ? 'Valid' : 'Invalid';
    },
    resetRequiredGeneralForm() {
      this.practicalExamples.requiredGeneral = { line1: '', line2: '', postcode: '', city: '', state: '', country: '' };
      this.$refs.requiredGeneralForm.resetValidation();
    },
    // General Optional
    validateOptionalGeneralForm() {
      const isValid = this.$refs.optionalGeneralForm.validate();
      this.optionalGeneralFormValidationResult = isValid ? 'Valid' : 'Invalid';
    },
    resetOptionalGeneralForm() {
      this.practicalExamples.optionalGeneral = { line1: '', line2: '', postcode: '', city: '', state: '', country: '' };
      this.$refs.optionalGeneralForm.resetValidation();
    }
  }
}

// Complete Type & Validation Matrix:
// ┌─────────────────┬─────────────────┬─────────────────┐
// │                 │   Required      │   Optional      │
// ├─────────────────┼─────────────────┼─────────────────┤
// │ Malaysia Type   │ ✅ States dropdown │ ✅ States dropdown │
// │ (type="malaysia")│ ✅ Auto "Malaysia"  │ ✅ Auto "Malaysia"  │
// │                 │ ✅ 5-digit postcode │ ✅ 5-digit postcode │
// │                 │ ✅ Required fields  │ ⚪ No validation    │
// ├─────────────────┼─────────────────┼─────────────────┤
// │ General Type    │ ✅ Text state input │ ✅ Text state input │
// │ (type="general") │ ✅ Manual country   │ ✅ Manual country   │
// │                 │ ✅ Flexible postcode│ ✅ Flexible postcode│
// │                 │ ✅ Required fields  │ ⚪ No validation    │
// └─────────────────┴─────────────────┴─────────────────┘`,
      malaysiaTypeCode: `// Malaysia-Specific Address Input
// Template (Pug syntax)
yz-address-input(
  :line1.sync="address.line1"
  :line2.sync="address.line2"
  :postcode.sync="address.postcode"
  :city.sync="address.city"
  :state.sync="address.state"
  :country.sync="address.country"
  type="malaysia"
  required
)

// Script - Data structure
data() {
  return {
    address: {
      line1: '',
      line2: '',
      postcode: '',
      city: '',
      state: '',
      country: '' // Auto-set to "Malaysia"
    }
  }
}

// Malaysia-specific features when type="malaysia":
// ✅ State field becomes dropdown with 16 Malaysian states
// ✅ Country auto-set to "Malaysia" (read-only)
// ✅ Postcode validates exactly 5 digits
// ✅ Auto-lookup city/state when valid postcode entered
// ✅ API call to /shipping/postcodes?postcode={value}

// Sample Malaysian postcodes for testing:
// 50200 (Kuala Lumpur), 10400 (Georgetown), 80100 (Johor Bahru)
// 20000 (Kuala Terengganu), 93350 (Kuching), 05000 (Alor Setar)`,
      generalTypeCode: `// General International Address Input
// Template (Pug syntax)
yz-address-input(
  :line1.sync="address.line1"
  :line2.sync="address.line2"
  :postcode.sync="address.postcode"
  :city.sync="address.city"
  :state.sync="address.state"
  :country.sync="address.country"
  type="general"
  required
)

// Script - Data structure
data() {
  return {
    address: {
      line1: '',
      line2: '',
      postcode: '',
      city: '',
      state: '',      // Free text input
      country: ''     // Manual entry
    }
  }
}

// General international features when type="general":
// ✅ State field as flexible text input (not dropdown)
// ✅ Country field allows manual entry
// ✅ Flexible postcode validation (no strict format)
// ✅ Still supports 5-digit postcode lookup if applicable
// ✅ Works with any international address format

// International examples:
// USA: state="California", country="USA", postcode="94102"
// UK: state="England", country="UK", postcode="SW1A 1AA"
// Canada: state="Ontario", country="Canada", postcode="M5H 2N2"`,
    }
  },
  computed: {
    addressInputProps() {
      return [
        {
          name: 'line1',
          type: 'String',
          default: "''",
          description: 'First line of address (supports .sync modifier)'
        },
        {
          name: 'line2',
          type: 'String',
          default: "''",
          description: 'Second line of address (supports .sync modifier)'
        },
        {
          name: 'postcode',
          type: 'String',
          default: "''",
          description: 'Postcode field with auto-lookup (supports .sync modifier)'
        },
        {
          name: 'city',
          type: 'String',
          default: "''",
          description: 'City field (supports .sync modifier)'
        },
        {
          name: 'state',
          type: 'String',
          default: "''",
          description: 'State field with Malaysian states dropdown (supports .sync modifier)'
        },
        {
          name: 'country',
          type: 'String',
          default: "''",
          description: 'Country field (supports .sync modifier)'
        },
        {
          name: 'required',
          type: 'Boolean',
          default: 'false',
          description: 'Makes address required when any field contains value'
        },
        {
          name: 'readonly',
          type: 'Boolean',
          default: 'false',
          description: 'Makes all address fields read-only'
        },
        {
          name: 'disabled',
          type: 'Boolean',
          default: 'false',
          description: 'Disables all address fields'
        },
        {
          name: 'size',
          type: 'String',
          default: "'lg'",
          description: 'Size of all fields. Options: lg, md, sm'
        },
        {
          name: 'type',
          type: 'String',
          default: "'general'",
          description: 'Address input mode. Options: malaysia (Malaysian states dropdown, auto-set country, 5-digit postcode validation), general (flexible international addressing)'
        },
      ]
    },
    addressInputEvents() {
      return [
        {
          name: '@update:line1',
          parameters: 'value',
          description: 'Emitted when line1 changes (for .sync modifier)'
        },
        {
          name: '@update:line2',
          parameters: 'value',
          description: 'Emitted when line2 changes (for .sync modifier)'
        },
        {
          name: '@update:postcode',
          parameters: 'value',
          description: 'Emitted when postcode changes (for .sync modifier)'
        },
        {
          name: '@update:city',
          parameters: 'value',
          description: 'Emitted when city changes (for .sync modifier)'
        },
        {
          name: '@update:state',
          parameters: 'value',
          description: 'Emitted when state changes (for .sync modifier)'
        },
        {
          name: '@update:country',
          parameters: 'value',
          description: 'Emitted when country changes (for .sync modifier)'
        }
      ]
    },
    addressInputMethods() {
      return [
        {
          name: 'validate()',
          parameters: '-',
          returns: 'Boolean',
          description: 'Validates the address component based on required prop. Returns true if valid, false if invalid. Called automatically by v-form.'
        },
        {
          name: 'resetValidation()',
          parameters: '-',
          returns: 'void',
          description: 'Resets validation state (simplified implementation for v-form compatibility)'
        }
      ]
    },
    addressInputComputed() {
      return [
        {
          name: 'hasError',
          type: 'Boolean',
          description: 'Indicates if the component has validation errors. Returns true when required=true and some but not all required fields are filled.'
        }
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      // Update the route with the section hash
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    },
    validateCustomerForm() {
      if (this.$refs.customerForm) {
        const formArray = this.$refs.customerForm;
        const formRef = Array.isArray(formArray) ? formArray[0] : formArray;
        const isValid = formRef.validate();
        this.customerFormValidationResult = isValid ? 'Valid' : 'Invalid';
        setTimeout(() => {
          this.customerFormValidationResult = null;
        }, 2000);
      }
    },
    resetCustomerForm() {
      // Clear customer form data
      this.customerName = '';
      this.customerEmail = '';
      this.customerPhone = '';
      this.billingAddress = {
        line1: '',
        line2: '',
        postcode: '',
        city: '',
        state: ''
      };
      this.shippingAddress = {
        line1: '',
        line2: '',
        postcode: '',
        city: '',
        state: ''
      };
      this.sameAsBilling = false;

      if (this.$refs.customerForm) {
        this.$refs.customerForm.resetValidation();
        this.customerFormValidationResult = null;
      }
    },
    setPostcode(postcode) {
      this.postcodeAddress.postcode = postcode;
    },
    setMalaysiaPostcode(type, postcode) {
      this.malaysiaAddresses[type].postcode = postcode;
    },
    setInternationalSample(type, sample) {
      this.generalAddresses[type] = {
        line1: `${sample.city} Street Address`,
        line2: 'Unit/Suite (optional)',
        postcode: sample.postcode,
        city: sample.city,
        state: sample.state,
        country: sample.country
      };
    },
    // Practical validation methods
    validateRequiredMalaysiaForm() {
      if (this.$refs.requiredMalaysiaForm) {
        const formArray = this.$refs.requiredMalaysiaForm;
        const formRef = Array.isArray(formArray) ? formArray[0] : formArray;
        const isValid = formRef.validate();
        this.requiredMalaysiaFormValidationResult = isValid ? 'Valid' : 'Invalid';
        setTimeout(() => {
          this.requiredMalaysiaFormValidationResult = null;
        }, 2000);
      }
    },
    resetRequiredMalaysiaForm() {
      this.practicalExamples.requiredMalaysia = {
        line1: '',
        line2: '',
        postcode: '',
        city: '',
        state: '',
        country: 'Malaysia'
      };
      if (this.$refs.requiredMalaysiaForm) {
        this.$refs.requiredMalaysiaForm.resetValidation();
        this.requiredMalaysiaFormValidationResult = null;
      }
    },
    validateOptionalMalaysiaForm() {
      if (this.$refs.optionalMalaysiaForm) {
        const formArray = this.$refs.optionalMalaysiaForm;
        const formRef = Array.isArray(formArray) ? formArray[0] : formArray;
        const isValid = formRef.validate();
        this.optionalMalaysiaFormValidationResult = isValid ? 'Valid' : 'Invalid';
        setTimeout(() => {
          this.optionalMalaysiaFormValidationResult = null;
        }, 2000);
      }
    },
    resetOptionalMalaysiaForm() {
      this.practicalExamples.optionalMalaysia = {
        line1: '',
        line2: '',
        postcode: '',
        city: '',
        state: '',
        country: 'Malaysia'
      };
      if (this.$refs.optionalMalaysiaForm) {
        this.$refs.optionalMalaysiaForm.resetValidation();
        this.optionalMalaysiaFormValidationResult = null;
      }
    },
    validateRequiredGeneralForm() {
      if (this.$refs.requiredGeneralForm) {
        const formArray = this.$refs.requiredGeneralForm;
        const formRef = Array.isArray(formArray) ? formArray[0] : formArray;
        const isValid = formRef.validate();
        this.requiredGeneralFormValidationResult = isValid ? 'Valid' : 'Invalid';
        setTimeout(() => {
          this.requiredGeneralFormValidationResult = null;
        }, 2000);
      }
    },
    resetRequiredGeneralForm() {
      this.practicalExamples.requiredGeneral = {
        line1: '',
        line2: '',
        postcode: '',
        city: '',
        state: '',
        country: ''
      };
      if (this.$refs.requiredGeneralForm) {
        this.$refs.requiredGeneralForm.resetValidation();
        this.requiredGeneralFormValidationResult = null;
      }
    },
    validateOptionalGeneralForm() {
      if (this.$refs.optionalGeneralForm) {
        const formArray = this.$refs.optionalGeneralForm;
        const formRef = Array.isArray(formArray) ? formArray[0] : formArray;
        const isValid = formRef.validate();
        this.optionalGeneralFormValidationResult = isValid ? 'Valid' : 'Invalid';
        setTimeout(() => {
          this.optionalGeneralFormValidationResult = null;
        }, 2000);
      }
    },
    resetOptionalGeneralForm() {
      this.practicalExamples.optionalGeneral = {
        line1: '',
        line2: '',
        postcode: '',
        city: '',
        state: '',
        country: ''
      };
      if (this.$refs.optionalGeneralForm) {
        this.$refs.optionalGeneralForm.resetValidation();
        this.optionalGeneralFormValidationResult = null;
      }
    },
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: #fafafa;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}

.gap-2 > * + * {
  margin-left: 0.5rem;
}

.gap-4 > * + * {
  margin-top: 1rem;
}
</style>